import {
  searchExcursionsButtonRequest,
  searchExcursionsRequest,
  searchFacetsRequest,
  searchItineraryButtonRequest,
  searchItineraryMultifilterRequest,
  searchItineraryRequest,
} from '../stateManager/slices/searchSlice';
import { AppDispatch, store } from '../stateManager/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  buildFacetsOnlyQuery,
  buildMultiFilterQueryString,
  buildSelectedFilterExcursionsQueryString,
  buildSelectedFilterQueryString,
} from './buildSelectedFilterQuery';
import { PaginationVariationsEnum } from '../../../../interfaces/src/lib/enums/index';
import { SortingFilterByState } from '../stateManager/slices/sortingFilterBySlice';
import { SortingViewPriceState } from '../stateManager/slices/sortingViewPriceSlice';
import { IContainedFilter } from '../stateManager/slices/multiFilterSlice';
import { getHitsPerPage } from '../utilFuncs/contextAwareUtils';
import { isDealsPage, useDealsFilters, useDealsPromotions } from '../utilFuncs/dealsManagement';
import { useDynamicFilters } from '../utilFuncs/dynamicPortsManagement';
import { getContextVariables } from '../genericUtils/algoliaContextUtils';

dayjs.extend(utc);

const previousSelectedFilters = {
  default: '',
  button: '',
  multifilter: '',
  facets: '',
};
let sortingState: SortingFilterByState | null = null;
let sortingViewPrice: SortingViewPriceState | null = null;
let previousActivePageIndex = 1;

async function fetchAdobeECID(): Promise<string> {
  try {
    const isLocal = window.location.hostname === 'localhost';
    if (typeof (window as any).alloyUK !== 'function' || isLocal) {
      return '';
    }

    const ecid = '';

    return ecid;
  } catch (e) {
    console.warn('[Adobe ECID] Error fetching ECID:', e);
    return '';
  }
}

async function handleSearchRequest(
  isForFilterUpdate = false,
  dispatch: AppDispatch,
  filterType: 'default' | 'button' | 'multifilter' | 'facets',
  buildQueryParams: () => string | string[],
  requestAction: (params: any) => any,
  facets: string[] = ['*']
) {
  const { activePageIndex, hitsNumberPerPage, paginationMode } = store.getState().pagination;
  const sortingStateUpdated = store.getState().sortingFilterBy;
  const sortingViewPriceUpdated = store.getState().sortingViewPrice;

  const selectedFilters = buildQueryParams();
  const { algoliaContextComponent } = store.getState().algoliaContextComponent;

  if (
    filterType !== 'default' &&
    previousSelectedFilters[filterType] !== '' &&
    selectedFilters === previousSelectedFilters[filterType] &&
    sortingState === sortingStateUpdated &&
    sortingViewPrice === sortingViewPriceUpdated &&
    previousActivePageIndex === activePageIndex
  ) {
    return;
  }
  // Hits per page
  let hitsPerPage = 8;
  if (filterType === 'default' && !isForFilterUpdate) {
    hitsPerPage =
      paginationMode === PaginationVariationsEnum.LOAD_MORE ? hitsNumberPerPage : getHitsPerPage();
  } else if (
    filterType === 'button' ||
    filterType === 'multifilter' ||
    filterType === 'facets' ||
    isForFilterUpdate
  ) {
    hitsPerPage = 0;
  }

  const context = getContextVariables(algoliaContextComponent, isDealsPage());

  const searchParams = {
    distinct: 1,
    hitsPerPage: hitsPerPage,
    pageFromRequest: paginationMode === PaginationVariationsEnum.LOAD_MORE ? 0 : activePageIndex,
    filters: selectedFilters,
    facets: facets,
    context,
    clickAnalytics: true,
    userToken: await fetchAdobeECID(),
  };

  if (paginationMode && !isNaN(searchParams.hitsPerPage) && !isNaN(searchParams.pageFromRequest)) {
    previousSelectedFilters[filterType] = selectedFilters as string;
    sortingState = sortingStateUpdated;
    sortingViewPrice = sortingViewPriceUpdated;
    previousActivePageIndex = activePageIndex;

    dispatch(requestAction(searchParams));
  }
}

export function handleSearch(dispatch: AppDispatch, isForFilterUpdate = false) {
  const indexType = store.getState().searchIndexTypeState.searchIndexType;

  if (indexType) {
    const isExcursions = indexType === 'excursions';
    const searchRequest = isExcursions ? searchExcursionsRequest : searchItineraryRequest;
    useDealsFilters(dispatch);
    useDealsPromotions(dispatch);
    if (isExcursions) {
      useDynamicFilters(dispatch);
      handleSearchRequest(
        isForFilterUpdate,
        dispatch,
        'default',
        () => buildSelectedFilterExcursionsQueryString(false, false),
        searchRequest
      );
    } else {
      handleSearchRequest(
        isForFilterUpdate,
        dispatch,
        'default',
        () => buildSelectedFilterQueryString(false, false, false, true),
        searchRequest
      );
    }
  }
}

export function handleWishlistSearch(dispatch: AppDispatch) {
  handleSearchRequest(
    false,
    dispatch,
    'default',
    () => buildSelectedFilterQueryString(false, false, false, false, true),
    searchItineraryRequest
  );
}

export function handleSearchButton(dispatch: AppDispatch) {
  const indexTypeButton = store.getState().searchIndexTypeState.searchIndexType;
  if (indexTypeButton) {
    const isExcursions = indexTypeButton === 'excursions';

    const searchRequest =
      indexTypeButton === 'excursions'
        ? searchExcursionsButtonRequest
        : searchItineraryButtonRequest;
    if (isExcursions) {
      handleSearchRequest(
        false,
        dispatch,
        'button',
        () => buildSelectedFilterExcursionsQueryString(true, false),
        searchRequest
      );
    } else {
      handleSearchRequest(
        false,
        dispatch,
        'button',
        () => buildSelectedFilterQueryString(false, true, false),
        searchRequest
      );
    }
  }
}

export function handleSearchMultifilter(dispatch: AppDispatch) {
  const moreFilters = store.getState().multiFilter.moreFilters;
  const facets: string[] = [];
  const dealsPage = isDealsPage();

  handleSearchRequest(
    false,
    dispatch,
    'multifilter',
    () => {
      return moreFilters.map((filter: IContainedFilter) => {
        const filterType =
          dealsPage && filter.filterType === 'promotion' ? 'dealpromotion' : filter.filterType;
        facets.push(filterType);
        return buildMultiFilterQueryString(filter.filterType, false);
      });
    },
    searchItineraryMultifilterRequest,
    facets
  );
}

export function handleSearchAllFacets(dispatch: AppDispatch) {
  handleSearchRequest(true, dispatch, 'facets', () => buildFacetsOnlyQuery(), searchFacetsRequest);
}

/** Facet attributes for departure airport only (used by deals airport filter). */
const FACETS_AIRPORT_ONLY = ['flights.departureAirportCode', 'flights.departureAirportName'];

/**
 * Facets-only request requesting only departure airport code and name.
 * Use for deals pages with airport filter so the extra call returns response for all the airports only.
 */
export function handleSearchAllFacetsAirportOnly(dispatch: AppDispatch) {
  handleSearchRequest(
    true,
    dispatch,
    'facets',
    () => buildFacetsOnlyQuery(),
    searchFacetsRequest,
    FACETS_AIRPORT_ONLY
  );
}
